import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { translateBlogContent, detectLanguage } from '../services/translation';

export interface BlogTranslationHook {
  translateBlog: (title: string, content: string) => Promise<{ title: string; content: string }>;
  isTranslating: boolean;
  translationError: string | null;
  detectSourceLanguage: (text: string) => 'cs' | 'en';
  getTargetLanguage: () => 'cs' | 'en';
}

export const useBlogTranslation = (): BlogTranslationHook => {
  const { i18n } = useTranslation();
  const [isTranslating, setIsTranslating] = useState(false);
  const [translationError, setTranslationError] = useState<string | null>(null);

  const detectSourceLanguage = (text: string): 'cs' | 'en' => {
    return detectLanguage(text);
  };

  const getTargetLanguage = (): 'cs' | 'en' => {
    // If current language is Czech, translate to English, and vice versa
    return i18n.language === 'cs' ? 'en' : 'cs';
  };

  const translateBlog = async (title: string, content: string): Promise<{ title: string; content: string }> => {
    setIsTranslating(true);
    setTranslationError(null);

    try {
      const sourceLang = detectSourceLanguage(title + ' ' + content);
      const targetLang = getTargetLanguage();

      // Don't translate if source and target are the same
      if (sourceLang === targetLang) {
        return { title, content };
      }

      const result = await translateBlogContent(title, content, sourceLang, targetLang);
      
      return {
        title: result.translatedTitle,
        content: result.translatedContent,
      };
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Translation failed';
      setTranslationError(errorMessage);
      throw error;
    } finally {
      setIsTranslating(false);
    }
  };

  return {
    translateBlog,
    isTranslating,
    translationError,
    detectSourceLanguage,
    getTargetLanguage,
  };
};
